import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyContent, CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/konze/konze.png",
        title: "How KONZE streamlined lead generation with Gallabox",
        sector: "Technology & Education Solutions",
        externalLink: "/case-study/konze",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/til-limited/til-limited.png",
        title: "TIL Limited boosts lead generation and customer engagement with Gallabox",
        sector: "Manufacturing",
        externalLink: "/case-study/til-limited",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua.png",
        title: "Inlingua doubles growth by streamlining student engagement with Gallabox",
        sector: "Education",
        externalLink: "/case-study/inlingua",
    },
];

const GlobalViewsMENA: React.FC = () => {
    return (
        <NewLayout
            title="Global Views MENA's growth story: From manual chaos to seamless messaging | Case Study"
            description="Discover how Global Views MENA replaced weeks of manual messaging with automated, personalized WhatsApp campaigns — boosting productivity, user reach, and revenue."
            canonical="https://gallabox.com/case-study/global-views"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/global-views/global-views-og.png" }}
        >
            <CaseStudyHerosection
                title="When every message matters: How Global Views MENA scaled customer engagement effortlessly"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/global-views/global-views-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "90%", label: "boost in team productivity" },
                    { percentage: "70%", label: "rise in customer satisfaction" },
                    { percentage: "60%", label: "increase in user base" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Retail & Distribution"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcasts" },
                                    { link: "https://gallabox.com/click-to-whatsapp-ads", label: "Click-to-WhatsApp Ads" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <CaseStudyContent>
                        <section id="About Global Views MENA">
                            <CaseStudyInformation subTitle={"About Global Views MENA"} className="mt-0" />
                            <p className="mt-2">
                                Global Views MENA is a premium furniture and home decor brand serving clients across the Middle East and North Africa. With a growing catalog and
                                customer base, the company needed an efficient way to communicate promotions, product launches, and offers to thousands of customers simultaneously.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Manual messaging overload",
                                        nestedListItem: [
                                            "Prior to Gallabox, product updates and marketing communications were sent manually to each customer. This process was highly time-consuming, often taking weeks to complete, and left room for errors and inconsistencies.",
                                        ],
                                    },
                                    {
                                        listItem: "Lack of monitoring and structure",
                                        nestedListItem: [
                                            "Without automation, there was no system to track which customers had received messages or whether follow-ups were required. This lack of oversight created inefficiencies and hindered the team's ability to close deals promptly.",
                                        ],
                                    },
                                    {
                                        listItem: "Inefficient use of staff time",
                                        nestedListItem: [
                                            "The manual workflow consumed significant staff hours that could have been used for higher-value activities, such as customer engagement and closing sales. The team needed a solution that could automate repetitive tasks while maintaining a human touch in communication.",
                                        ],
                                    },
                                ]}
                                className="mt-2 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-12" />
                            <p className="mt-2">
                                Global Views MENA tried using Wati.io but found it inadequate for their needs. Manual messaging and spreadsheet tracking were labor-intensive,
                                error-prone, and difficult to scale. Despite exploring other solutions, the team was unable to find a tool that combined automation with reliable
                                support and personalized messaging.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why Global Views MENA chose Gallabox"} className="mt-0" />
                            <p className="mt-2">Gallabox stood out because of:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Responsive sales and support teams:",
                                        nestedListItem: ["The support and sales teams provided immediate responses, demos, and guidance."],
                                    },
                                    {
                                        listItem: "Automation for scale:",
                                        nestedListItem: ["Broadcasts and click-to-WhatsApp campaigns allowed messaging at scale without losing personalization."],
                                    },
                                    {
                                        listItem: "Ease of use:",
                                        nestedListItem: ["Teams could quickly adopt the platform, integrating it seamlessly into their workflow."],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <p className="mt-5">These factors made Gallabox the ideal partner for both efficiency and customer engagement.</p>
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-12" />
                            <p className="mt-2">Global Views MENA implemented Gallabox to:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Streamline customer communication, making messages more consistent and reliable." },
                                    { listItem: "Retain existing customers through timely updates and engagement." },
                                    { listItem: "Improve team productivity, freeing staff from repetitive tasks." },
                                    { listItem: "Close deals faster, with structured messaging and timely follow-ups." },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on business growth"} className="mt-0" />
                            <p className="mt-2">With Gallabox, the results were transformative:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "100% improvement in overall business growth, as automation accelerated communications and conversions.",
                                    },
                                    {
                                        listItem: "90% increase in employee productivity, with less manual effort required for outreach.",
                                    },
                                    {
                                        listItem: "70% growth in customer satisfaction, with faster response times and structured engagement.",
                                    },
                                    {
                                        listItem: "60% increase in user base, reflecting the ability to reach and nurture more customers effectively.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                By automating product updates and campaign messages, Global Views MENA not only improved reach and responsiveness but also reduced the time spent on
                                manual tasks.
                            </p>

                            <CaseStudyInformation subTitle={"Building stronger customer relationships"} className="mt-10" />
                            <p className="mt-2">
                                Gallabox helped Global Views MENA replace manual effort with smart automation — creating a faster, more connected, and customer-friendly
                                communication model.
                            </p>
                            <p className="mt-5">
                                Today, the brand engages customers proactively through WhatsApp, responds instantly to inquiries, and drives repeat business through better service
                                and timely follow-ups.
                            </p>
                            <p className="mt-5">
                                Gallabox&apos;s reliable automation and responsive team have become key pillars of Global Views MENA&apos;s continued growth and success.
                            </p>
                        </section>
                    </CaseStudyContent>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default GlobalViewsMENA;
